<script setup>
import { ref, computed, onMounted } from 'vue'
import { useGlobalStore } from '@stores/global'
import { useEventStore } from '@stores/event'
import Loading from '@components/global/Loading.vue'
import Hero from '@sections/global/Hero.vue'
import ContentFrame from '@components/global/ContentFrame.vue'
import ContentSection from '@components/global/ContentSection.vue'
import ReportLeaderboard from '@components/global/ReportLeaderboard.vue'
import QualificationBanner from '@components/player/QualificationBanner.vue'
import Input from '@components/forms/Input.vue'
import Textarea from '@components/forms/Textarea.vue'
import Button from '@components/forms/Button.vue'
import Select from '@components/forms/Select.vue'

const global = useGlobalStore()

const filters = ref({})

const pointsRankings = computed(() => {
	return global.games.filter((game) => game.slug === filters.value.game)[0]?.points_rankings ?? []
})

const filteredItems = computed(() => {
	return pointsRankings.value.filter((item) => {
		const searchTerm = (filters.value.search || '').toLowerCase()
		const searchMatch = !searchTerm || item.player.username.toLowerCase().includes(searchTerm)
		return searchMatch
	})
})

/* This leaderboard only holds SWC 2026 scores, so it only banners SWC 2026
   qualifications. Add or swap event names here if that changes. Matches the
   event's short name, or its full name where no short name is set. */
const bannerEvents = ['SWC 2026']

const bannerQualifications = (item) => item.qualifications.filter((qualification) => bannerEvents.includes(qualification.event))

const clear = () => {
	filters.value = {}
}

onMounted(() => {
	if (global.games.length > 0) {
		filters.value.game = global.games[0].slug
	} else {
		global.getGames().then(() => {
			if (global.games.length > 0) {
				filters.value.game = global.games[0].slug
			}
		})
	}
})
</script>

<template>
	<Loading v-if="global.games?.length === 0" />
	<template v-else>
		<Hero title="Points Ranking" description="See who's at the top of the SNK Esports world." />
		<section>
			<div class="container">
				<div class="relative flex items-center gap-3">
					<!-- <div class="hidden items-center gap-3 sm:flex">
						<Toggle :options="views" v-model="view" />
						<div class="mx-2 h-12 w-px border-r border-black/15"></div>
					</div> -->
					<div class="flex grow flex-col gap-3 md:flex-row">
						<Input class="secondary w-full" v-model="filters.search" placeholder="Search" />
						<Select class="secondary w-full" v-model="filters.game">
							<template v-for="game in global.games" :key="game.id">
								<option :value="game.slug" v-if="game.points_rankings.length > 0">{{ game.name }}</option>
							</template>
						</Select>
						<!-- <Select class="secondary w-full" :options="event.regions" v-model="filters.region" placeholder="Region" /> -->
					</div>
					<div class="mx-2 h-12 w-px border-r border-black/15"></div>
					<div class="flex gap-3">
						<Button class="secondary" @click="clear">Clear</Button>
					</div>
				</div>
			</div>
		</section>
		<section class="my-10">
			<div class="container">
				<ContentFrame class="overflow-x-scroll">
					<span class="p-1 text-sm">★ = Qualified for SWC 2026</span>
					<!-- <ReportLeaderboard href="https://docs.google.com/forms/d/e/1FAIpQLSe7TzqVSM7qGuhYuRqKNidNyHkPQwhK0SR-uGFw4rsItwVr4Q/viewform" /> -->
					<table>
						<thead>
							<tr>
								<th>Rank</th>
								<th class="min-w-48">Player</th>
								<th>Total</th>
								<th>Legend</th>
								<th class="text-nowrap">Master 1</th>
								<th class="text-nowrap">Master 2</th>
								<th class="text-nowrap">Master 3</th>
								<th>Online</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in filteredItems" v-if="filteredItems.length > 0">
								<td :class="[{ first: item.rank === 1 }, { second: item.rank === 2 }, { third: item.rank === 3 }]">
									<div class="flex items-center gap-3">
										<i v-if="item.rank <= 3" :class="['fas fa-trophy leading-none', { 'text-[#DEBF77]': item.rank === 1 }, { 'text-[#C2C2C2]': item.rank === 2 }, { 'text-[#D39881]': item.rank === 3 }]" />
										#{{ item.rank }}
									</div>
								</td>
								<td class="py-4">
									<div class="flex items-center gap-3 text-nowrap">
										<img :src="item.player.avatar" class="from-snk-light-900 size-10 rounded-full bg-linear-to-t to-white object-cover object-top md:size-16" v-if="item.player.avatar" />
										<div class="flex flex-col items-start gap-1.5">
											<!-- Only a player with a profile page is worth linking. -->
											<router-link class="text-snk-blue-500 hover:text-snk-blue-700 font-semibold transition-colors duration-200" :to="{ name: 'player', params: { slug: item.player.slug } }" v-if="item.player.profile">{{ item.player.username }}</router-link>
											<template v-else>{{ item.player.username }}</template>
											<QualificationBanner compact v-for="qualification in bannerQualifications(item)" :key="`${qualification.event}-${qualification.via}`" :title="qualification.event" :label="qualification.via ? `via ${qualification.via}` : null" />
										</div>
									</div>
								</td>
								<!-- <td>{{ item.player.country.flag }} {{ item.player.country.slug }}</td> -->
								<td>{{ item.total_points.toLocaleString('en-GB') }}</td>
								<td>{{ item.legend.toLocaleString('en-GB') }}</td>
								<td>{{ item.master_1.toLocaleString('en-GB') }}</td>
								<td>{{ item.master_2.toLocaleString('en-GB') }}</td>
								<td>{{ item.master_3.toLocaleString('en-GB') }}</td>
								<td>{{ item.online.toLocaleString('en-GB') }}</td>
							</tr>
							<tr v-else>
								<td colspan="8" class="text-center">There are no players matching your filters.</td>
							</tr>
						</tbody>
					</table>
				</ContentFrame>
			</div>
		</section>
	</template>
</template>

<style scoped>
@reference '../../css/theme.css';

.list-leave-active {
	@apply absolute;
}
.grid-leave-active {
	@apply absolute;
}
</style>
